import { useCallback, useMemo, useRef, useState } from 'react';

import { type Environment } from 'react-relay';

import { AnyAri } from '@atlassian/ari/any-ari';
import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { useUnitLinkedResourceIds } from '@atlassian/unit-linked-resources';

import type { Product } from './global-object-preview-panels';

const anchorBuilder: Partial<Record<Product, (siteId: string) => string>> = {
	jira: (siteId) => JiraSiteAri.create({ siteId }).toString(),
	confluence: (siteId) => ConfluenceSiteAri.create({ siteId }).toString(),
};

const buildAnchorAri = (product: Product, cloudId: string): string => {
	const build = anchorBuilder[product];
	return build && cloudId ? build(cloudId) : '';
};

const extractLinkedCloudIds = (linkedResourceIds: string[]): Set<string> => {
	const cloudIds = new Set<string>();
	for (const ari of linkedResourceIds) {
		try {
			const parsed = AnyAri.parse(ari);
			if (parsed.resourceType === 'site' && parsed.resourceId) {
				cloudIds.add(parsed.resourceId);
			}
		} catch {
			// Ignore unparseable ARIs.
		}
	}
	return cloudIds;
};

interface UseUnitLinkedResourcesResult {
	/**
	 * Returns `true` if `targetCloudId` belongs to the same unit as the current
	 * cloudId. Fails open (returns `true`) when the target matches the current
	 * cloudId, when the target has no cloudId, on the first cross-unit check that
	 * triggers the deferred fetch, and while that fetch is loading or errored.
	 */
	isCloudIdInSameUnit: (targetCloudId: string | undefined) => boolean;
}

/**
 * Resolves whether a target cloudId belongs to the current site's unit, via the
 * `admin_unitLinkedResource` GraphQL query.
 *
 * The query is fetched **lazily**: it does not run on mount. It is only triggered
 * the first time {@link UseUnitLinkedResourcesResult.isCloudIdInSameUnit} is called
 * with a genuine cross-unit target (a truthy cloudId that differs from the current
 * one, when an anchor ARI exists). Pages that never evaluate a cross-unit preview
 * panel therefore never issue the query.
 *
 * This hook is only reached behind the `preview_panel_unit_check` gate: the
 * top-level `useGlobalObjectPreviewPanels` selects `useGlobalObjectPreviewPanelsNew`
 * (its only caller) via `functionWithCondition`, so no additional gating is needed
 * here.
 */
export const useUnitLinkedResources = (
	relayEnvironment: Environment,
	product: Product,
	cloudId: string,
): UseUnitLinkedResourcesResult => {
	const anchorAri = useMemo(() => buildAnchorAri(product, cloudId), [product, cloudId]);

	const [hasRequestedFetch, setHasRequestedFetch] = useState(false);

	const { linkedResourceIds, loading, error } = useUnitLinkedResourceIds(
		relayEnvironment,
		anchorAri,
		{ enabled: hasRequestedFetch },
	);

	const linkedCloudIds = useMemo(
		() => extractLinkedCloudIds(linkedResourceIds),
		[linkedResourceIds],
	);

	// Read via a ref so isCloudIdInSameUnit keeps a stable identity across fetches.
	const stateRef = useRef({ linkedCloudIds, loading, error, hasRequestedFetch });
	stateRef.current = { linkedCloudIds, loading, error, hasRequestedFetch };

	const fetchScheduledRef = useRef(false);

	const isCloudIdInSameUnit = useCallback(
		(targetCloudId: string | undefined): boolean => {
			const { linkedCloudIds, loading, error, hasRequestedFetch } = stateRef.current;

			if (!anchorAri || !targetCloudId || targetCloudId === cloudId) {
				return true;
			}

			// This runs during the consumer's render, where React forbids setState.
			// Defer it to a microtask so the fetch is triggered after render finishes.
			if (!hasRequestedFetch && !fetchScheduledRef.current) {
				fetchScheduledRef.current = true;
				queueMicrotask(() => setHasRequestedFetch(true));
			}

			// Fail open until the fetch has been requested and settled with data.
			if (!hasRequestedFetch || loading || error) {
				return true;
			}

			return linkedCloudIds.has(targetCloudId);
		},
		[anchorAri, cloudId],
	);

	return { isCloudIdInSameUnit };
};
